import React, { useReducer, useEffect, useContext, useCallback } from "react";
import { fetchChapter } from "../../api/BibleApi";

import { Chapter } from "api/typedefs/Bible";
import { useScriptureRouting } from "components/Bible/useScriptureRouting";
import { useNetworkStatus } from "utils/hooks";
import { useBible } from "components/Bible/BibleContainer";
import { useBibleDownloads } from "storage/BibleDownloader/BibleDownloadContext";

interface ChaptersByUid {
  [chapterUid: string]: StateChapter | undefined;
}

interface ChapterStatus {
  loading: boolean;
  error: boolean;
  notFound: boolean;
}

interface ChapterStatuses {
  [chapterId: string]: ChapterStatus | undefined;
}

export interface StateChapter extends Omit<Chapter, "parallels"> {}

export interface ChapterCtx {
  chapters: {
    [chapterUid: string]: StateChapter | undefined;
  };
  getChapter: (params?: {
    chapterUid?: string;
    bibleId?: string;
    chapterId?: string;
  }) => StateChapter | undefined;
  chapterStatuses: ChapterStatuses;
}

const getChapterUid = (bibleId: string, chapterId: string) =>
  bibleId + ":" + chapterId;

const ChapterContext = React.createContext<ChapterCtx>({
  chapters: {},
  chapterStatuses: {},
  getChapter: () => undefined
});

const defaultChapterStatus = { loading: false, error: false, notFound: false };

const loadChapter = (params: {
  bibleIds: string[];
  chapterId: string;
  setChapterStatuses: (
    chapterUids: string[],
    chapterStatus: ChapterStatus
  ) => void;
  setChapters: (chaptersByUid: ChaptersByUid) => void;
}): Promise<void> => {
  const { bibleIds, chapterId, setChapterStatuses, setChapters } = params;
  if (bibleIds.length === 0) return Promise.resolve();
  const chapterUids = bibleIds.map(id => getChapterUid(id, chapterId));

  setChapterStatuses(chapterUids, { ...defaultChapterStatus, loading: true });

  const [bibleId, ...parallels] = bibleIds;
  return fetchChapter({
    bibleId,
    chapterId,
    parallels
  })
    .then(chapterRes => {
      if (!chapterRes) {
        return setChapterStatuses(chapterUids, {
          ...defaultChapterStatus,
          error: true,
          notFound: true
        });
      }
      const { next, previous, parallels = [], ...chapter } = chapterRes;
      const chapters: ChaptersByUid = [chapter, ...parallels].reduce(
        (chapters, chapter) => ({
          ...chapters,
          [getChapterUid(chapter.bibleId, chapter.id)]: {
            ...chapter,
            next,
            previous
          }
        }),
        {}
      );
      setChapters(chapters);
    })
    .catch(err => {
      console.error("fetchChapter err:", err);
      setChapterStatuses(chapterUids, { ...defaultChapterStatus, error: true });
    });
};

interface ChapterCtxState {
  chapters: ChaptersByUid;
  chapterStatuses: ChapterStatuses;
  allowPrefetching: boolean;
}

interface ChapterCtxAction {
  type: string;
  payload?: any;
}

const initialState: ChapterCtxState = {
  chapters: {},
  chapterStatuses: {},
  allowPrefetching: false
};

function chapterCtxReducer(state: ChapterCtxState, action: ChapterCtxAction) {
  switch (action.type) {
    case "ALLOW_PREFETCHING":
      return { ...state, allowPrefetching: true };
    case "SET_CHAPTERS": {
      const { chaptersByUid } = action.payload;
      const chapterUids = Object.keys(chaptersByUid);
      return {
        ...state,
        chapters: { ...state.chapters, ...chaptersByUid },
        chapterStatus: chapterUids.reduce(
          (acc: ChapterStatuses, uid: string) => ({
            ...acc,
            [uid]: { ...defaultChapterStatus }
          }),
          { ...state.chapterStatuses }
        )
      };
    }
    case "SET_CHAPTER_STATUSES": {
      const { chapterUids, status } = action.payload;
      return {
        ...state,
        chapterStatuses: chapterUids.reduce(
          (acc: ChapterStatuses, uid: string) => ({ ...acc, [uid]: status }),
          { ...state.chapterStatuses }
        )
      };
    }
    default:
      console.error("invalid type");
      return state;
  }
}

// NOTE: skipping cache is currently a bit naive if fetching for multiple bibles,
// if one isn't loaded into state, it makes a request for all of them. ignoring
// for now, because these may need to be independent in light of new offlinefeature
const ChapterProvider: React.FC = ({ children }) => {
  const {
    bibleId,
    chapterId,
    parallelBibleIds,
    setChapter
  } = useScriptureRouting();
  const [state, dispatch] = useReducer(chapterCtxReducer, initialState);
  const { chapters, chapterStatuses, allowPrefetching } = state;

  const isOnline = useNetworkStatus();
  const { biblesById } = useBible();
  const { offlineBibleIds } = useBibleDownloads();

  const currBible = biblesById[bibleId]; // bible of route's main bibleId
  const currChapter = chapters[getChapterUid(bibleId, chapterId)]; // chapter of route's main bibleId and the chapterId

  // splits out chapters by their location (network, storage, component state)
  // and gets the chapters from the cheapest locations state > storage > network
  const loadAllChapters = useCallback(
    async (chapterId: string) => {
      if (!chapterId) return;

      // dedupe bibleIds to avoid 2 reqs for the same bible or larger network reqs
      const bibleIds = [...new Set([bibleId, ...parallelBibleIds])];
      // creates list of chapters not available in state
      // and keys on the location of where the chapters can be found
      const { networkBibleIds, storageBibleIds } = bibleIds.reduce(
        (acc, bibleId) => {
          const uid = getChapterUid(bibleId, chapterId);
          // if we have the chapter or have some state of the chapter
          const isInState = uid in chapters;
          const hasStatus = Object.values(chapterStatuses[uid] || {}).includes(
            true
          );
          const isOfflineBible = offlineBibleIds.includes(bibleId);

          if (isInState || hasStatus) {
            // do nothing
          } else if (isOfflineBible) {
            acc.storageBibleIds.push(bibleId);
          } else if (isOnline) {
            acc.networkBibleIds.push(bibleId);
          } else {
            // we're offline and can't do anything...
          }

          return acc;
        },
        { networkBibleIds: [] as string[], storageBibleIds: [] as string[] } // [networkBibleIds, storageBibleIds]
      );

      const requests = [];
      const baseParms = {
        isOnline,
        setChapterStatuses: (chapterUids: string[], status: ChapterStatus) => {
          dispatch({
            type: "SET_CHAPTER_STATUSES",
            payload: { chapterUids, status }
          });
        },
        setChapters: (chaptersByUid: ChaptersByUid) => {
          dispatch({
            type: "SET_CHAPTERS",
            payload: { chaptersByUid }
          });
        }
      };

      if (networkBibleIds.length) {
        requests.push(
          loadChapter({
            ...baseParms,
            bibleIds: [...networkBibleIds],
            chapterId
          })
        );
      }

      if (storageBibleIds.length) {
        storageBibleIds.forEach(bibleId =>
          requests.push(
            loadChapter({ ...baseParms, bibleIds: [bibleId], chapterId })
          )
        );
      }

      return Promise.all(requests);
    },
    [
      bibleId,
      isOnline,
      offlineBibleIds,
      parallelBibleIds,
      chapterStatuses,
      chapters
    ]
  );

  // fetch current chapter and prev/next if allowed and able
  useEffect(() => {
    loadAllChapters(chapterId).then(() => {
      dispatch({ type: "ALLOW_PREFETCHING" });
    });
  }, [loadAllChapters, chapterId]);

  useEffect(() => {
    if (!allowPrefetching || !currChapter) return;

    Promise.all([
      loadAllChapters(currChapter.previous?.id || ""),
      loadAllChapters(currChapter.next?.id || "")
    ]);
  }, [allowPrefetching, currChapter, loadAllChapters]);

  // redirect to first chapter in bible if current chapter not found
  const currFirstChapterId =
    currBible?.books?.[0].chapters?.find(
      chapter => !chapter.id.includes("intro")
    )?.id || "";
  const currChapterNotFound =
    chapterStatuses[getChapterUid(bibleId, chapterId)]?.notFound || false;
  useEffect(() => {
    if (!currChapterNotFound || !currFirstChapterId) return;
    setChapter({ chapterId: currFirstChapterId });
  }, [currChapterNotFound, currFirstChapterId, setChapter]);

  const getChapter = useCallback(
    (params?: {
      chapterUid?: string;
      bibleId?: string;
      chapterId?: string;
    }) => {
      const uid =
        params?.chapterUid ||
        getChapterUid(
          params?.bibleId || bibleId,
          params?.chapterId || chapterId
        );
      return chapters[uid];
    },
    [bibleId, chapterId, chapters]
  );

  return (
    <ChapterContext.Provider
      value={{
        chapters,
        chapterStatuses,
        getChapter
      }}
    >
      {children}
    </ChapterContext.Provider>
  );
};

export default ChapterProvider;

export const useChapter = () => useContext(ChapterContext);

export function withChapter<Props extends { chapter: ChapterCtx }>(
  WrappedComponent: React.ComponentType<Props>
) {
  return function ComponentWithChapter(props: any) {
    return (
      <ChapterContext.Consumer>
        {context => <WrappedComponent {...props} chapter={context} />}
      </ChapterContext.Consumer>
    );
  };
}
